import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';

import { InputFieldComponent } from '@shared/components/input-field/input-field.component';
import { SkeletonLoadingComponent } from '@shared/components/skeleton-loading/skeleton-loading.component';
import { LoadingService } from '@core/services/loading.service';
import { LoginFacadeService } from './login.facade';

@Component({
  selector: 'app-layout-login',
  standalone: true,
  imports: [ReactiveFormsModule, InputFieldComponent, SkeletonLoadingComponent],
  providers: [LoginFacadeService],
  templateUrl: './layout-login.component.html',
  styleUrl: './layout-login.component.scss',
})
export class LayoutLoginComponent implements OnInit, OnDestroy {
  private facade = inject(LoginFacadeService);
  private destroyRef = inject(DestroyRef);
  readonly loadingService = inject(LoadingService);

  loginForm!: FormGroup;
  readonly isPasswordVisible = signal<boolean>(false);
  readonly hasError = signal<boolean>(false);
  readonly isUsernameNotEmpty = signal<boolean>(false);
  readonly isPasswordNotEmpty = signal<boolean>(false);

  ngOnInit(): void {
    this.facade.logout();
    this.loginForm = this.facade.buildLoginForm();

    this.loginForm
      .get('username')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.isUsernameNotEmpty.set(!!value?.length);
        if (value) this.hasError.set(false);
      });

    this.loginForm
      .get('password')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.isPasswordNotEmpty.set(!!value?.length);
        if (value) this.hasError.set(false);
      });
  }

  ngOnDestroy(): void {
    this.loadingService.setLoading(false);
  }

  togglePasswordVisibility(): void {
    this.isPasswordVisible.update((v) => !v);
  }

  cleanField(): void {
    this.loginForm.get('username')?.setValue('');
  }

  resetForm(): void {
    this.loginForm.reset();
    this.isPasswordVisible.set(false);
    this.isUsernameNotEmpty.set(false);
    this.isPasswordNotEmpty.set(false);
  }

  onSubmit(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      this.hasError.set(true);
      return;
    }

    this.hasError.set(false);
    this.loadingService.setLoading(true);

    this.facade
      .login(this.loginForm)
      .pipe(finalize(() => this.loadingService.setLoading(false)))
      .subscribe({
        next: () => this.resetForm(),
        error: () => {
          this.resetForm();
          this.hasError.set(true);
        },
      });
  }
}
